import { CoachingEventStatus } from '../../coaching-base/general';
import { createReducer } from '../../store.model';
import { createTagInfo } from '../functions';

import { allowedAndEnabledActionsReducer } from './allowed-and-enabled-actions.reducer';
import { approvalReducer } from './approval.reducer';
import { commitmentDistillReducer } from './commitment-distill.reducer';
import {
    ActionType,
    RootCloseAction,
    RootCloseReasonAction,
    RootDeleteAction,
    RootDeleteResultAction,
    RootDismissMessageAction,
    RootDisputeAction,
    RootEditAction,
    RootPublishResultAction,
    RootReloadAction,
    RootReloadResultAction,
    RootRetrieveObservationSummaryAction,
    RootRetrieveObservationSummaryResultAction,
    RootSaveAction,
    RootSaveResultAction,
    RootSendApprovalAction,
    RootSendApprovalResultAction,
    RootSendNotificationAction,
    RootSendNotificationResultAction,
    RootSetTagAction,
    RootSignOffAction,
    RootSignOffApprovalAction,
    RootSignOffApprovalResultAction,
} from './commitment.action';
import { defaultState, RootState } from './commitment.state';
import { diffItemsInfoViewFromDiffTransformer } from './diff-items-info-view-from-diff.transformer';
import { DiffItemsInfoView } from './diff-items-info-view.model';
import { mirrorCopyAlertInfoViewTransformer } from './mirror-copy-alert-info-view.transformer';

const { builder, reducer } = createReducer<ActionType, RootState>(
    defaultState(),
);

builder
    .add<RootReloadAction>(
        ActionType.COACHING_COMMITMENT__ROOT__RELOAD,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                },
                justSaved: false,
                editingIntended: false,
                isEditing: false,
            };
        },
    )
    .add<RootReloadResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__RELOAD_RESULT,
        (state, action) => {
            const behaviours = action.payload.behaviours || state.behaviours;
            const subBehaviours =
                action.payload.subBehaviours || state.subBehaviours;
            const metrics = action.payload.metrics || state.metrics;

            const mirrorCopyAlertInfo = mirrorCopyAlertInfoViewTransformer(
                action.payload.commitment,
                action.payload.draftId,
                action.payload.edit,
            );

            let nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
                behaviours,
                subBehaviours,
                metrics,
                coachingEvent: action.payload.coachingEvent,
                noteEntity: action.payload.noteEntity,
                interactionEntity: action.payload.interactionEntity,
                mirrorCopyAlertInfo,
                draftId: action.payload.draftId,
                editingIntended: action.payload.edit,
                isEditing: action.payload.edit,
            };

            // always run so that that approval can be unset from state on reloads if it's null
            nextState = approvalReducer(
                nextState,
                action.payload.draft,
                action.payload.approval,
                action.payload.commitment,
                action.payload.commitment.latestEventParticipants,
                action.payload.goalMetricIdList,
                action.payload.goalsCollection,
            );

            // approvalReducer will call commitmentDistillReducer when approval and approval.commitment are set
            if (
                !action.payload.approval ||
                !action.payload.approval.commitment
            ) {
                nextState = commitmentDistillReducer(
                    nextState,
                    null,
                    action.payload.commitment,
                    action.payload.goalMetricIdList,
                    action.payload.goalsCollection,
                );
            }

            return nextState;
        },
    );

builder.add<RootSetTagAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SET_TAG,
    (state, action) => {
        return {
            ...state,
            tagInfo: createTagInfo(
                action.payload.focus,
                state.behaviours,
                state.subBehaviours,
            ),
        };
    },
);

builder
    .add<RootSaveAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SAVE,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    save: true,
                },
                justSaved: false,
            };
        },
    )
    .add<RootSaveResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SAVE_RESULT,
        (state, action) => {
            const mirrorCopyAlertInfo = mirrorCopyAlertInfoViewTransformer(
                action.payload.commitment,
                action.payload.draftId,
            );

            let nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    save: false,
                },
                justSaved: true,
                mirrorCopyAlertInfo,
                draftId: action.payload.draftId,
            };

            nextState = approvalReducer(
                nextState,
                action.payload.draft,
                null,
                action.payload.commitment,
                action.payload.commitment.latestEventParticipants,
                null,
                action.payload.goalsCollection,
            );

            return commitmentDistillReducer(
                nextState,
                null,
                action.payload.commitment,
                null,
                action.payload.goalsCollection,
            );
        },
    );

builder.add<RootPublishResultAction>(
    ActionType.COACHING_COMMITMENT__ROOT__PUBLISH_RESULT,
    (state, action) => {
        let next: DiffItemsInfoView;
        if (action.payload.open) {
            next = diffItemsInfoViewFromDiffTransformer(
                action.payload.diff,
                [state.performer, state.coach],
                state.coachingEvent.deliveryTypes,
                state.metrics,
            );
        }
        return {
            ...state,
            isPublishing: action.payload.open || undefined,
            diffItemsInfo: next,
        };
    },
);

builder.add<RootCloseReasonAction>(
    ActionType.COACHING_COMMITMENT__ROOT__CLOSE_REASON,
    (state, action) => {
        return {
            ...state,
            isClosingReason: action.payload.open || undefined,
        };
    },
);

builder.add<RootCloseAction>(
    ActionType.COACHING_COMMITMENT__ROOT__CLOSE,
    (state, action) => {
        return {
            ...state,
            closingCtx: action.payload.ctx || undefined,
            isClosing: action.payload.open || undefined,
        };
    },
);

builder.add<RootDisputeAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DISPUTE,
    (state, action) => {
        return {
            ...state,
            isDisputing: action.payload.open,
        };
    },
);

builder.add<RootSignOffAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF,
    (state, action) => {
        return {
            ...state,
            isSigningOff: action.payload.open || undefined,
            signingOffCtx: action.payload.ctx || undefined,
        };
    },
);

builder
    .add<RootSendNotificationAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SEND_NOTIFICATION,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    notification: true,
                },
            };
        },
    )
    .add<RootSendNotificationResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SEND_NOTIFICATION_RESULT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    notification: false,
                },
            };
        },
    );

builder.add<RootDismissMessageAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DISMISS_MESSAGE,
    (state, action) => {
        return {
            ...state,
            justSaved: false,
        };
    },
);

builder
    .add<RootRetrieveObservationSummaryAction>(
        ActionType.COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY,
        (state, action) => {
            const lastLinkedObservations = new Map(
                state.lastLinkedObservations,
            );
            lastLinkedObservations.delete(action.payload.observationId);

            return {
                ...state,
                lastLinkedObservations,
            };
        },
    )
    .add<RootRetrieveObservationSummaryResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY_RESULT,
        (state, action) => {
            const lastLinkedObservations = new Map(
                state.lastLinkedObservations,
            );
            lastLinkedObservations.set(
                action.payload.observationId,
                action.payload.observation,
            );

            return {
                ...state,
                lastLinkedObservations,
            };
        },
    );

builder.add<RootDeleteAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DELETE,
    (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                delete: true,
            },
        };
    },
);

builder.add<RootDeleteResultAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DELETE_RESULT,
    (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                delete: false,
            },
            commitment: {
                ...state.commitment,
                statusId: action.payload.success
                    ? CoachingEventStatus.DELETED
                    : state.commitment.statusId,
            },
        };
    },
);

builder.add<RootEditAction>(
    ActionType.COACHING_COMMITMENT__ROOT__EDIT,
    (state, action) => {
        const mirrorCopyAlertInfo = mirrorCopyAlertInfoViewTransformer(
            state.commitment,
            null,
            action.payload.edit,
        );
        const nextState: RootState = {
            ...state,
            editingIntended: action.payload.edit,
            isEditing: action.payload.edit,
            mirrorCopyAlertInfo,
        };
        return allowedAndEnabledActionsReducer(nextState);
    },
);

builder
    .add<RootSendApprovalAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SEND_APPROVAL,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    approval: true,
                },
            };
        },
    )
    .add<RootSendApprovalResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SEND_APPROVAL_RESULT,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    approval: false,
                },
                mirrorCopyAlertInfo: undefined,
                // disable form after change
                editingIntended: false,
                // disable form after change
                isEditing: false,
            };

            return approvalReducer(
                nextState,
                null,
                action.payload.approval,
                action.payload.approval.commitment,
                action.payload.approval.commitment.latestEventParticipants,
                null,
                action.payload.goalsCollection,
            );
        },
    );

builder
    .add<RootSignOffApprovalAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    approval: true,
                },
            };
        },
    )
    .add<RootSignOffApprovalResultAction>(
        ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL_RESULT,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    approval: false,
                },
                // disable form after change
                editingIntended: false,
                // disable form after change
                isEditing: false,
            };

            return approvalReducer(
                nextState,
                null,
                action.payload.approval,
                action.payload.approval.commitment,
                action.payload.approval.commitment.latestEventParticipants,
                null,
                action.payload.goalsCollection,
            );
        },
    );

export default {
    coachingCommitment: {
        root: reducer,
    },
};
